// Credit Notes data layer (Phase 4, P4-CC7) — corrections/reversals of ISSUED invoices.
//
// The SINGLE interface between the app and public.credit_notes + public.credit_note_items
// (supabase/migrations/20260712120000_credit_notes.sql).
//
// SECURITY / RULES (server-enforced; mirrored here):
//   • We NEVER send company_id or credit_note_number — DB triggers own them (company
//     forced/inherited; CN-00001 auto; both immutable). RLS scopes all.
//   • A credit note is DRAFT until issueCreditNote() flips it to ISSUED, which fires
//     the DB immutability + OVER-CREDIT guard (Σ credits ≤ invoice total, per VAT band).
//   • Only ISSUED credit notes affect the invoice_ledger (amount_outstanding / status).
//   • VAT math is the invoice computeTotals (imported, read-only) so credit VAT nets
//     invoice VAT per band exactly — the invoices data layer is not modified.
//   • Restock is delegated to inventory.syncCreditNote() — inventory stays the sole
//     owner of stock movements; the credit note never writes stock itself.
//
// RETURN-TYPE CONTRACT (consistent with the other modules):
//   collection → { data, count, page?, pageSize? } · single → T|null ·
//   create/issue/cancel → id · getters-for-parent → Array · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { computeTotals } from "./invoices";
import { syncCreditNote } from "./inventory";
import { todayISO } from "./dates";
import { C } from "../customers/theme";

const CN = "credit_notes";
const ITEMS = "credit_note_items";

export const DEFAULT_PAGE_SIZE = 25;

export const CN_STATUSES = [
  { value: "draft", label: "Draft", color: C.textMuted },
  { value: "issued", label: "Issued", color: C.success },
  { value: "cancelled", label: "Cancelled", color: C.danger },
];
export const CREDIT_REASONS = [
  { value: "return", label: "Goods returned" },
  { value: "correction", label: "Invoice error / correction" },
  { value: "discount", label: "Post-sale discount" },
  { value: "writeoff", label: "Write-off" },
  { value: "other", label: "Other" },
];
export const CN_SORTS = [
  { value: "date", label: "Issue Date" },
  { value: "number", label: "Credit No." },
  { value: "total", label: "Total" },
  { value: "status", label: "Status" },
  { value: "created", label: "Newest" },
];

const HEADER_WRITE_FIELDS = ["invoice_id", "customer_id", "customer_name", "issue_date", "reason", "notes"];
const LINE_WRITE_FIELDS = ["invoice_line_id", "product_id", "sku", "description"];
const SORT_COLUMNS = { date: "issue_date", number: "credit_note_number", total: "total_amount", status: "status", created: "created_at" };

const LIST_SELECT =
  "id, credit_note_number, invoice_id, customer_id, customer_name, issue_date, status, reason, " +
  "subtotal, total_vat, total_amount, created_at, updated_at, invoices ( invoice_number )";
const FULL_SELECT =
  "*, invoices ( id, invoice_number, total_amount ), " +
  "credit_note_items ( id, invoice_line_id, product_id, sku, description, quantity, unit_price, discount_percent, vat_rate, drs, drs_amount, line_total, restock, position )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = (error && error.message) || "This credit would exceed the invoice total or a VAT band, or a field is invalid."; break;
    case "42501": message = (error && error.message) || "This credit note is issued and locked."; break;
    case "23503": message = "The referenced invoice, line or product does not exist."; break;
    case "23505": message = "A credit note with this number already exists."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = "Credit note not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

function headerFrom(payload, totals) {
  return {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    status: "draft",
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat,
    total_drs: totals.total_drs, total_amount: totals.total_amount, vat_breakdown: totals.vat_breakdown,
  };
}

/** Insert a draft credit note's items (uniform rows; restock only on product lines). */
async function insertItems(cnId, lines) {
  const rows = (lines || []).map((l, i) => ({
    ...pick(l, LINE_WRITE_FIELDS),
    quantity: round3(l.quantity),
    unit_price: round2(l.unit_price),
    discount_percent: Number(l.discount_percent) || 0,
    vat_rate: Number(l.vat_rate) || 0,
    drs: !!l.drs,
    drs_amount: round2(l.drs_amount),
    line_total: round2(l.line_total),
    restock: !!l.restock && !!l.product_id,   // DB also enforces (check: not restock or product set)
    position: Number(l.position) || i,
    credit_note_id: cnId,
  }));
  if (rows.length) {
    const { error } = await client().from(ITEMS).insert(rows);
    if (error) throw toError(error, "Could not save credit note items");
  }
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Create a DRAFT credit note + its items. No ledger or stock effect while draft.
 * VAT/discount/DRS/totals are computed with the invoice computeTotals (per band).
 * @param {Object} payload header fields + `lines: Array` (each line may carry restock)
 * @returns {Promise<string>} the new credit note id.
 */
export async function createCreditNote(payload) {
  const c = client();
  const lines = (payload?.lines || []).filter((l) => l.product_id || (l.description && String(l.description).trim()) || Number(l.quantity) > 0);
  const totals = computeTotals(lines);
  const { data, error } = await c.from(CN).insert(headerFrom(payload, totals)).select("id").single();
  if (error) throw toError(error, "Could not create credit note");
  await insertItems(data.id, totals.lines);
  return data.id;
}

/**
 * Issue a draft credit note (draft → issued). The DB fires the immutability lock and
 * the over-credit guard; on success the credit applies to the invoice_ledger. Restock
 * lines then post inventory Return movements via inventory.syncCreditNote (idempotent).
 * @param {string} id
 * @returns {Promise<string>} the credit note id.
 */
export async function issueCreditNote(id) {
  const { error } = await client().from(CN).update({ status: "issued" }).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not issue the credit note");
  await syncCreditNote(id);   // no-op unless the CN has restock lines; owns all stock effects
  return id;
}

/**
 * Cancel (discard) a DRAFT credit note — items cascade. Issued credit notes are
 * immutable legal documents and cannot be cancelled here.
 * @param {string} id
 * @returns {Promise<string>} the credit note id.
 */
export async function cancelDraft(id) {
  const c = client();
  const { data: cn, error: gErr } = await c.from(CN).select("status").eq("id", id).maybeSingle();
  if (gErr) throw toError(gErr, "Could not load the credit note");
  if (!cn) throw toError({ code: "PGRST116" }, "Credit note not found");
  if (cn.status !== "draft") { const e = new Error("Only draft credit notes can be cancelled."); e.code = "NOT_DRAFT"; throw e; }
  const { error } = await c.from(CN).delete().eq("id", id);
  if (error) throw toError(error, "Could not cancel the draft");
  return id;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List credit notes (RLS-scoped) with search, status + invoice filters, sort, pagination.
 * @param {Object} [opts]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getCreditNotes(opts = {}) {
  const { search, status, invoiceId, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "date", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "issue_date";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  let q = c.from(CN).select(LIST_SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, from + safeSize - 1);
  if (status && status !== "all") q = q.eq("status", status);
  if (invoiceId) q = q.eq("invoice_id", invoiceId);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`credit_note_number.ilike.%${term}%,customer_name.ilike.%${term}%,reason.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load credit notes");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

/** Full credit note (header + invoice + items, ordered), or null. */
export async function getCreditNote(id) {
  const { data, error } = await client().from(CN).select(FULL_SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load credit note");
  if (data?.credit_note_items) data.credit_note_items.sort((a, b) => (a.position || 0) - (b.position || 0));
  return data || null;
}

/** All credit notes for an invoice (newest first) — for the Invoice view's linked list. */
export async function getCreditNotesForInvoice(invoiceId) {
  const { data, error } = await client().from(CN).select(LIST_SELECT).eq("invoice_id", invoiceId).order("created_at", { ascending: false });
  if (error) throw toError(error, "Could not load credit notes for the invoice");
  return data || [];
}

/**
 * P4-CC19b — KPIs across ALL credit notes, aggregated SERVER-SIDE
 * (public.credit_note_stats, 20260811120000_cc19b_aggregates.sql). Previously a
 * client-side sum over a fetch PostgREST silently capped at 1,000 rows.
 *
 * The month key is passed IN (browser-local calendar), never derived
 * server-side — Postgres is UTC. See dates.js / P4-CC20.
 *
 * `issuedVat` was dropped in CC19b — nothing consumed it.
 * @returns {Promise<{issuedTotal:number, issuedThisMonth:number, issuedCount:number, draftCount:number, count:number}>}
 */
export async function getCreditNoteStats() {
  const { data, error } = await client().rpc("credit_note_stats", { p_month: todayISO().slice(0, 7) });
  if (error) throw toError(error, "Could not load credit note stats");
  const r = (data || [])[0] || {};
  const n = (v) => Number(v) || 0;
  return {
    issuedTotal: n(r.issued_total),
    issuedThisMonth: n(r.issued_this_month),
    issuedCount: n(r.issued_count),
    draftCount: n(r.draft_count),
    count: n(r.total_count),
  };
}

/**
 * TEMPORARY (P4-CC19b Phase 1) — pre-CC19b implementation, kept ONLY for
 * supabase/verify/cc19b_aggregates.verify.mjs to diff against the SQL version.
 * Carries the silent 1,000-row truncation; nothing in src/ may call it.
 * DELETE once the parity harness has been run and accepted.
 */
export async function getCreditNoteStatsLegacy() {
  const { data, error } = await client().from(CN).select("status, total_amount, total_vat, issue_date").range(0, 9999);
  if (error) throw toError(error, "Could not load credit note stats");
  const rows = data || [];
  const month = todayISO().slice(0, 7);
  let issuedTotal = 0, issuedThisMonth = 0, issuedVat = 0, issuedCount = 0, draftCount = 0;
  for (const r of rows) {
    if (r.status === "issued") {
      issuedTotal += Number(r.total_amount) || 0; issuedVat += Number(r.total_vat) || 0; issuedCount += 1;
      if ((r.issue_date || "").slice(0, 7) === month) issuedThisMonth += Number(r.total_amount) || 0;
    } else if (r.status === "draft") draftCount += 1;
  }
  return { issuedTotal: round2(issuedTotal), issuedThisMonth: round2(issuedThisMonth), issuedVat: round2(issuedVat), issuedCount, draftCount, count: rows.length };
}

// ── P4-CC26c-1c — posting to the general ledger ──────────────────────────────

// Ledger refusals carry codes the generic toError() does not know about, and
// each sentence carries an INSTRUCTION the caller can act on. Mirror of
// invoices.js/payments.js toLedgerError: switch on the code, never on the
// message, and PASS THE DATABASE'S SENTENCE THROUGH VERBATIM — "Post the
// invoice first", "Reverse the relevant receipt first", "already been posted"
// are the actionable detail, and replacing them with generic text is
// CC26c-1g's disease (the same defect CC26c-1b-fix2 removed from payments.js).
export function toLedgerError(error) {
  const code = error && error.code;
  const raw = (error && error.message) || "";
  let message;
  switch (code) {
    case "23505":
      // The database's own sentence already says it plainly.
      message = raw || "This credit note has already been posted to the ledger.";
      break;
    case "42501":
      // A draft/cancelled credit note, or no write permission on accounts.
      message = raw || "This credit note cannot be posted.";
      break;
    case "P0002":
      // The function raises the deliberately AMBIGUOUS sentence itself (no id,
      // no internal name), so passing it through preserves the ambiguity.
      message = raw || "Credit note not found, or you do not have access to it.";
      break;
    case "P0001":
      // Three shapes: does not reconcile · parent invoice not posted · the
      // cash-basis refund guard. Each names exactly what is wrong and what to
      // do next; a friendlier sentence would throw that away.
      message = raw || "This credit note could not be posted to the ledger.";
      break;
    case "23514":
      // Every check constraint on journals/journal_lines — carry the
      // constraint name through; it is the one identifying detail.
      message = `The ledger entry for this credit note was rejected by the database. This is a defect, not a data-entry problem — please report it, with this detail: ${raw || "(the database supplied no detail)"}`;
      break;
    default:
      message = raw || "Could not post the credit note to the ledger.";
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

/**
 * Post an ISSUED credit note to the general ledger (P4-CC26c-1c).
 * Dr Sales / Dr VAT (deferred on the cash basis) / Dr DRS / Cr Trade Debtors —
 * the mirror of the invoice posting. The database is the guard: issued-only,
 * parent invoice must be posted, no double posting, cash-basis refund guard.
 * @param {string} id credit note id
 * @returns {Promise<string>} the journal id
 */
export async function postToLedger(id) {
  const { data, error } = await client().rpc("post_credit_note_to_ledger", { p_credit_note_id: id });
  if (error) throw toLedgerError(error);
  return data;
}
